import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { EnvelopeSimple, DownloadSimple, PaperPlaneTilt, Eye, CheckCircle, Warning, ShieldCheck } from "@phosphor-icons/react";
import LoadFailedPanel from "@/components/LoadFailedPanel";

/**
 * Admin · CEO Onboarding — bulk mail-merge invite to CasePrime.
 * CEO-only. Includes: cheat-sheet PDF download, preview-then-send flow with
 * per-user password rotation, and Resend telemetry.
 */
export default function AdminOnboarding() {
  const { user: me } = useAuth();
  const isCEO = (me?.ux_tier || 0) >= 5;

  const [candidates, setCandidates] = useState([]);
  const [loadErr, setLoadErr] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [variant, setVariant] = useState("warm");
  const [resetPassword, setResetPassword] = useState(true);
  const [attachSheet, setAttachSheet] = useState(true);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewSubject, setPreviewSubject] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [diag, setDiag] = useState(null);
  const [diagBusy, setDiagBusy] = useState(false);

  const load = useCallback(() => {
    setLoadErr(null);
    api.get("/admin/onboarding/candidates")
      .then(r => setCandidates(r.data.users || []))
      .catch(e => setLoadErr(e));
  }, []);

  useEffect(() => { if (isCEO) load(); }, [isCEO, load]);

  const grouped = useMemo(() => {
    const g = { 5: [], 4: [], 3: [], 2: [], 1: [] };
    for (const u of candidates) { const t = u.ux_tier || 1; if (g[t]) g[t].push(u); }
    return g;
  }, [candidates]);

  const toggle = (uid) => {
    const next = new Set(selected);
    if (next.has(uid)) next.delete(uid); else next.add(uid);
    setSelected(next);
  };

  const selectTier = (tier) => {
    const uids = grouped[tier].map(u => u.user_id);
    const allSelected = uids.every(u => selected.has(u));
    const next = new Set(selected);
    if (allSelected) uids.forEach(u => next.delete(u));
    else uids.forEach(u => next.add(u));
    setSelected(next);
  };

  const selectAll = () => {
    if (selected.size === candidates.length) setSelected(new Set());
    else setSelected(new Set(candidates.map(u => u.user_id)));
  };

  const downloadSheet = async () => {
    try {
      const r = await api.get("/admin/onboarding/cheat-sheet.pdf", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([r.data]));
      a.download = "SPAN_CasePrime_QuickStart.pdf";
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
  };

  const runDiagnostics = async () => {
    setDiagBusy(true); setDiag(null); setErr(null);
    try {
      const { data } = await api.get("/admin/onboarding/email-diagnostics");
      setDiag(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setDiagBusy(false); }
  };

  const preview = async () => {
    if (selected.size === 0) { setErr("Select at least one user first."); return; }
    setErr(null); setBusy(true);
    try {
      const uid = Array.from(selected)[0];
      const { data } = await api.post("/admin/onboarding/preview", { user_id: uid, variant });
      setPreviewHtml(data.html);
      setPreviewSubject(data.subject);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const sendBatch = async () => {
    if (selected.size === 0) { setErr("Select at least one user first."); return; }
    const names = candidates.filter(u => selected.has(u.user_id))
                            .slice(0, 5).map(u => u.name).join(", ");
    if (!window.confirm(
      `Send onboarding email to ${selected.size} user(s)?\n\n` +
      `First few: ${names}${selected.size > 5 ? "…" : ""}\n\n` +
      (resetPassword
        ? "⚠️ Each recipient's password will be RESET to a fresh temporary seed embedded in their email.\n" +
          "They will be forced to change it on next login.\n\n"
        : "")+
      `Variant: ${variant}\n` +
      `Cheat sheet attached: ${attachSheet ? "yes" : "no"}\n\n` +
      "Continue?"
    )) return;

    setBusy(true); setErr(null); setResult(null);
    try {
      const { data } = await api.post("/admin/onboarding/send-batch", {
        user_ids: Array.from(selected),
        variant,
        reset_password: resetPassword,
        attach_cheat_sheet: attachSheet,
        confirmation: "CONFIRM-SEND-ONBOARDING",
      });
      setResult(data);
      load(); // refresh onboarding_email_sent_at flags
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  if (!isCEO) return (
    <div className="p-6" data-testid="onb-gate">
      <div className="card-flat p-6 border border-amber-200 bg-amber-50 text-amber-900 text-[13px]">
        CEO only — this tool sends outbound email + rotates passwords in bulk.
      </div>
    </div>
  );
  if (loadErr) return <div className="p-6"><LoadFailedPanel error={loadErr} resource="Onboarding candidates" onRetry={load} /></div>;

  return (
    <div className="p-6 space-y-5 max-w-6xl" data-testid="onboarding-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Admin · Onboarding</div>
          <h1 className="font-display text-[28px] font-bold tracking-tight mt-1">Invite the team to CasePrime</h1>
          <div className="text-[12px] text-[var(--text-muted)] mt-1">
            One-click bulk mail-merge · rotates each user&apos;s password to a fresh seed · Resend-tracked delivery · attaches the branded quick-start PDF.
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={runDiagnostics} disabled={diagBusy} data-testid="onb-run-diagnostics"
            className="span-btn span-btn-ghost h-9 text-[12px] inline-flex items-center gap-1.5 disabled:opacity-60">
            <ShieldCheck size={13} /> {diagBusy ? "Checking…" : "Check email config"}
          </button>
          <button onClick={downloadSheet} data-testid="dl-cheat-sheet"
            className="span-btn span-btn-ghost h-9 text-[12px] inline-flex items-center gap-1.5">
            <DownloadSimple size={13} /> Download cheat-sheet PDF
          </button>
        </div>
      </div>

      {diag && (
        <section
          data-testid="onb-diagnostics"
          className={`card-flat p-4 border ${diag.ok ? "border-emerald-200 bg-emerald-50" : "border-red-300 bg-red-50"}`}>
          <div className="flex items-start gap-2">
            {diag.ok ? <CheckCircle size={18} className="text-emerald-700 mt-0.5" />
                     : <Warning size={18} className="text-red-700 mt-0.5" />}
            <div className="min-w-0 flex-1">
              <div className={`font-semibold text-[13px] ${diag.ok ? "text-emerald-900" : "text-red-900"}`}>
                {diag.ok ? "Email configuration looks good" : "Email configuration needs a fix"}
              </div>
              <div className="text-[12px] mt-1 whitespace-pre-line">{diag.message}</div>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-[11px]">
                <div><strong>Sender in use:</strong> <span className="font-mono">{diag.sender || "(missing)"}</span></div>
                <div><strong>Sender domain:</strong> <span className="font-mono">{diag.sender_domain || "—"}</span></div>
                <div><strong>Resend key:</strong> <span className="font-mono">{diag.resend_api_key_masked}</span></div>
                <div><strong>Resend reachable:</strong> {diag.resend_reachable ? "yes" : "no"}</div>
              </div>
              {diag.verified_domains?.length > 0 && (
                <div className="text-[11px] mt-2">
                  <strong>Verified domains on this Resend account:</strong>
                  <ul className="pl-4 list-disc mt-0.5">
                    {diag.verified_domains.map(d => (
                      <li key={d.name} className="font-mono">
                        {d.name}{" "}
                        <span className={d.status === "verified" ? "text-emerald-700" : "text-amber-700"}>({d.status})</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="text-[11px] text-[var(--text-muted)] mt-2">{diag.hint}</div>
            </div>
          </div>
        </section>
      )}

      {/* ── Step 1: pick recipients ────────────────────────────────────── */}
      <section className="card-flat p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">Step 1 — Pick recipients</div>
          <div className="text-[12px]">
            <button onClick={selectAll} data-testid="onb-select-all"
              className="text-[var(--brand-primary)] hover:underline">
              {selected.size === candidates.length ? "Deselect all" : `Select all (${candidates.length})`}
            </button>
          </div>
        </div>

        {[5, 4, 3, 2, 1].map(tier => grouped[tier].length > 0 && (
          <div key={tier} data-testid={`onb-tier-${tier}`}>
            <div className="flex items-center gap-2 mb-1.5">
              <button onClick={() => selectTier(tier)}
                data-testid={`onb-tier-toggle-${tier}`}
                className="text-[10px] uppercase tracking-wider text-[var(--brand-primary)] hover:underline">
                Tier {tier} · {grouped[tier].length} user(s) · toggle
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {grouped[tier].map(u => {
                const isSel = selected.has(u.user_id);
                const sentBefore = !!u.onboarding_email_sent_at;
                return (
                  <label key={u.user_id}
                    data-testid={`onb-user-${u.user_id}`}
                    className={`flex items-start gap-2 p-2.5 rounded border cursor-pointer text-[12px] ${isSel ? "border-[var(--brand-primary)] bg-[var(--surface-2)]" : "border-[var(--border)] bg-[var(--surface,#fff)]"}`}>
                    <input type="checkbox" checked={isSel} onChange={() => toggle(u.user_id)}
                      data-testid={`onb-user-cb-${u.user_id}`}
                      className="mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate">{u.name || "—"}</div>
                      <div className="text-[10px] text-[var(--text-muted)] truncate">{u.email}</div>
                      <div className="text-[10px] text-[var(--text-muted)] truncate">{u.designation || u.department || "—"}</div>
                      {sentBefore && <div className="text-[10px] text-emerald-700 mt-0.5">✓ previously sent</div>}
                      {u.must_change_password && !sentBefore && <div className="text-[10px] text-amber-700 mt-0.5">never signed in</div>}
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </section>

      {/* ── Step 2: options ────────────────────────────────────────────── */}
      <section className="card-flat p-5 space-y-3">
        <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">Step 2 — Message options</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[13px]">
          <div>
            <label className="block text-[11px] font-semibold mb-1">Tone</label>
            <div className="flex gap-2">
              {[{ v: "warm", label: "Warm / personal" }, { v: "formal", label: "Corporate / formal" }].map(o => (
                <button key={o.v} onClick={() => setVariant(o.v)}
                  data-testid={`onb-variant-${o.v}`}
                  className={`flex-1 px-3 py-2 rounded border text-[12px] ${variant === o.v ? "border-[var(--brand-primary)] bg-[var(--surface-2)] font-semibold" : "border-[var(--border)]"}`}>
                  {o.label}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2 pt-1">
            <label className="flex items-start gap-2">
              <input type="checkbox" checked={resetPassword} onChange={e => setResetPassword(e.target.checked)}
                data-testid="onb-reset-pw" className="mt-0.5" />
              <span><strong>Rotate password</strong> — assign a fresh seed to each recipient (must_change_password=true)</span>
            </label>
            <label className="flex items-start gap-2">
              <input type="checkbox" checked={attachSheet} onChange={e => setAttachSheet(e.target.checked)}
                data-testid="onb-attach-sheet" className="mt-0.5" />
              <span><strong>Attach quick-start PDF</strong> — branded 1-page cheat sheet</span>
            </label>
          </div>
        </div>
      </section>

      {/* ── Step 3: preview + send ─────────────────────────────────────── */}
      <section className="card-flat p-5 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">Step 3 — Preview and send</div>
          <div className="text-[11px] text-[var(--text-muted)]">
            {selected.size} recipient(s) selected
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={preview} disabled={busy || selected.size === 0}
            data-testid="onb-preview"
            className="span-btn span-btn-ghost h-9 text-[12px] inline-flex items-center gap-1.5 disabled:opacity-40">
            Preview first recipient&apos;s email
          </button>
          <button onClick={sendBatch} disabled={busy || selected.size === 0}
            data-testid="onb-send"
            className="span-btn h-9 text-[12px] inline-flex items-center gap-1.5 disabled:opacity-40">
            <PaperPlaneTilt size={13} /> {busy ? "Sending…" : `Send to ${selected.size}`}
          </button>
        </div>

        {previewHtml && (
          <div data-testid="onb-preview-frame" className="border border-[var(--border)] rounded overflow-hidden">
            <div className="bg-[var(--surface-2)] px-3 py-1.5 text-[11px] text-[var(--text-muted)] border-b border-[var(--border)]">
              <strong>Subject:</strong> {previewSubject}
            </div>
            <iframe title="preview" srcDoc={previewHtml}
              className="w-full h-[620px] bg-white" sandbox="allow-same-origin" />
          </div>
        )}
      </section>

      {err && (
        <div className="text-[12px] text-red-800 bg-red-50 border border-red-200 px-3 py-2 rounded" data-testid="onb-error">
          <Warning size={14} className="inline mr-1" />{String(err)}
        </div>
      )}

      {result && (
        <section className="card-flat p-5 border border-emerald-200 bg-emerald-50 text-emerald-900" data-testid="onb-result">
          <div className="font-bold flex items-center gap-1.5 mb-2">
            <CheckCircle size={16} /> Batch {result.batch_id} sent · {result.sent} of {result.total} delivered ({result.failed} failed)
          </div>
          <details className="text-[12px]">
            <summary className="cursor-pointer font-semibold">Per-recipient breakdown</summary>
            <ul className="mt-1 pl-4 list-disc space-y-0.5">
              {result.results.map(r => (
                <li key={r.user_id}>
                  <strong>{r.name}</strong> ({r.email}) —{" "}
                  {r.status === "sent"
                    ? <span className="text-emerald-800">✓ sent{r.message_id ? ` · ${r.message_id.slice(0, 12)}…` : ""}{r.password_reset ? " · pw rotated" : ""}</span>
                    : <span className="text-red-700">✗ {r.error || "failed"}{r.sender_used ? <em className="block text-[10px] text-red-600">sender used: {r.sender_used}</em> : null}</span>}
                </li>
              ))}
            </ul>
          </details>
        </section>
      )}

      <div className="text-[11px] text-[var(--text-muted)] flex items-start gap-1.5 max-w-2xl">
        <ShieldCheck size={12} className="mt-0.5 shrink-0" />
        <span>All batches are audit-logged. Delivery events flow into <em>email_events</em> and are visible in the Command Center&apos;s email analytics tile. Resend&apos;s open/click webhook (already configured) updates the same event in-place.</span>
      </div>
    </div>
  );
}
